import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, Calendar, FileText, Newspaper, FolderKanban, Image, Users, Globe, Building2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageSEO from '../components/PageSEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
  return url;
};

const TYPE_ICONS = {
  news: Newspaper, events: Calendar, projects: FolderKanban, gallery: Image,
  documents: FileText, services: Building2, diaspora: Globe, pages: FileText, council: Users,
};

const TYPE_COLORS = {
  news: 'bg-emerald-100 text-emerald-700', events: 'bg-blue-100 text-blue-700',
  projects: 'bg-orange-100 text-orange-700', gallery: 'bg-purple-100 text-purple-700',
  documents: 'bg-gray-100 text-gray-700', services: 'bg-teal-100 text-teal-700',
  diaspora: 'bg-yellow-100 text-yellow-700', pages: 'bg-indigo-100 text-indigo-700',
  council: 'bg-rose-100 text-rose-700',
};

const FILTER_TYPES = [
  { value: '', label: 'Tous les types' },
  { value: 'news', label: 'Actualités' },
  { value: 'events', label: 'Événements' },
  { value: 'projects', label: 'Projets' },
  { value: 'gallery', label: 'Médiathèque' },
  { value: 'documents', label: 'Documents' },
  { value: 'services', label: 'Services' },
  { value: 'diaspora', label: 'Diaspora' },
  { value: 'pages', label: 'Pages' },
  { value: 'council', label: 'Conseil Municipal' },
];

const highlightText = (text, query) => {
  if (!text || !query) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
      : part
  );
};

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const typeFilter = searchParams.get('type') || '';
  const categoryFilter = searchParams.get('category') || '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(query);
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);

  const doSearch = useCallback(async () => {
    if (!query) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: query, page: currentPage, limit: 15 });
      if (typeFilter) params.set('type', typeFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await axios.get(`${API}/search?${params}`);
      setResults(res.data.results);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
      const cats = [...new Set(res.data.results.map(r => r.category).filter(Boolean))];
      setCategories(cats);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, typeFilter, categoryFilter, dateFrom, dateTo, currentPage]);

  useEffect(() => { doSearch(); }, [doSearch]);
  useEffect(() => { setSearchInput(query); }, [query]);

  const updateParams = (updates) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v) p.set(k, v); else p.delete(k);
    });
    if (updates.type !== undefined || updates.category !== undefined || updates.q !== undefined) p.set('page', '1');
    setSearchParams(p);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) updateParams({ q: searchInput.trim() });
  };

  return (
    <main className="min-h-screen bg-neutral-50" data-testid="search-results-page">
      <PageSEO title={`Recherche : ${query} - Gandiaye.sn`} description={`Résultats de recherche pour "${query}"`} />

      {/* Search Header */}
      <div className="bg-white border-b sticky top-[70px] z-30">
        <div className="container-main py-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Rechercher sur gandiaye.sn..."
                className="pl-10 h-12 text-base"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700" data-testid="search-submit-btn">
              Rechercher
            </Button>
            <Button type="button" variant="outline" className="h-12" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
              <Filter size={18} className="mr-2" /> Filtres
            </Button>
          </form>

          {showFilters && (
            <div className="mt-4 p-4 bg-neutral-50 rounded-lg grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="search-filters">
              <div>
                <label className="text-xs font-medium text-neutral-500 mb-1 block">Type</label>
                <Select value={typeFilter} onValueChange={(v) => updateParams({ type: v === 'all' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Tous les types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {FILTER_TYPES.filter(t => t.value).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {categories.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-neutral-500 mb-1 block">Catégorie</label>
                  <Select value={categoryFilter} onValueChange={(v) => updateParams({ category: v === 'all' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Toutes" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-neutral-500 mb-1 block">Du</label>
                <Input type="date" value={dateFrom} onChange={(e) => updateParams({ date_from: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-500 mb-1 block">Au</label>
                <Input type="date" value={dateTo} onChange={(e) => updateParams({ date_to: e.target.value })} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="container-main py-8">
        {query && (
          <p className="text-neutral-600 mb-6" data-testid="search-results-count">
            {loading ? 'Recherche en cours...' : (
              total > 0 
                ? <><strong>{total}</strong> résultat{total > 1 ? 's' : ''} pour <strong>"{query}"</strong></> 
                : <>Aucun résultat pour <strong>"{query}"</strong></>
            )}
          </p>
        )}

        {/* Active filters */}
        {(typeFilter || categoryFilter || dateFrom || dateTo) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {typeFilter && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                {FILTER_TYPES.find(t => t.value === typeFilter)?.label}
                <button onClick={() => updateParams({ type: '' })}><X size={14} /></button>
              </span>
            )}
            {categoryFilter && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                {categoryFilter}
                <button onClick={() => updateParams({ category: '' })}><X size={14} /></button>
              </span>
            )}
            {(dateFrom || dateTo) && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                {dateFrom && `Du ${dateFrom}`} {dateTo && `au ${dateTo}`}
                <button onClick={() => updateParams({ date_from: '', date_to: '' })}><X size={14} /></button>
              </span>
            )}
          </div>
        )}

        <div className="space-y-4">
          {results.map((result) => {
            const Icon = TYPE_ICONS[result.type] || FileText;
            const colorClass = TYPE_COLORS[result.type] || 'bg-gray-100 text-gray-700';
            const imageUrl = resolveUrl(result.image_url);

            return (
              <Card key={`${result.type}-${result.id}`} className="hover:shadow-md transition-shadow" data-testid={`search-result-${result.type}-${result.id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {imageUrl && (
                      <img src={imageUrl} alt="" className="w-20 h-20 object-cover rounded-lg flex-shrink-0" loading="lazy" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                          <Icon size={12} /> {result.type_label}
                        </span>
                        {result.date && (
                          <span className="text-xs text-neutral-400">{new Date(result.date).toLocaleDateString('fr-FR')}</span>
                        )}
                        {result.category && (
                          <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">{result.category}</span>
                        )}
                      </div>
                      {result.url ? (
                        <Link to={result.url} className="text-lg font-semibold text-neutral-800 hover:text-emerald-600 transition-colors line-clamp-1">
                          {highlightText(result.title, query)}
                        </Link>
                      ) : (
                        <p className="text-lg font-semibold text-neutral-800 line-clamp-1">{highlightText(result.title, query)}</p>
                      )}
                      {result.description && (
                        <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{highlightText(result.description, query)}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* No results state */}
        {!loading && query && results.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-700 mb-2">Aucun résultat trouvé</h3>
            <p className="text-neutral-500">Essayez avec d'autres termes ou modifiez les filtres</p>
          </div>
        )}

        {/* Empty state */}
        {!query && (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-700 mb-2">Rechercher sur Gandiaye.sn</h3>
            <p className="text-neutral-500">Tapez votre recherche pour trouver des actualités, événements, projets, documents...</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8" data-testid="search-pagination">
            <Button variant="outline" size="sm" disabled={currentPage <= 1}
              onClick={() => updateParams({ page: String(currentPage - 1) })}>
              <ChevronLeft size={16} /> Précédent
            </Button>
            <span className="text-sm text-neutral-600 px-4">Page {currentPage} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages}
              onClick={() => updateParams({ page: String(currentPage + 1) })}>
              Suivant <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
